// An instant as a person reads it on a screen, in their account's zone and
// labeled with it (`zone.ts`) — and never naming the wrong week.
//
// THE RULE, ONE COPY. A weekday alone ("Wed 3:00 AM") names a single day only
// while the instant is less than six days from the moment it is read: in that
// span no weekday comes round twice. Past it, the same weekday next week is
// the likelier reading, so the date goes in ("Wed, Oct 7, 3:00 AM"). A player
// dropped on a Tuesday in a league that runs waivers on Wednesdays clears the
// Wednesday AFTER next, and "until Wednesday 3:00 AM" read that Tuesday named
// tomorrow — on the drop sheet, the rows, the card, the wire and every claim's
// run (the review of W8, 2026-09-25). Past instants follow the same rule: a
// log line from three weeks ago is not "Fri 7:58 AM".
//
// PURE: the moment it is read is handed in. The server hands the instant it
// answers at, a screen the moment it draws, the simulator its own clock.

import { formatInZone } from './zone';

/** Under this far from now, a weekday alone names one day. */
export const WEEKDAY_ALONE_MS = 6 * 24 * 3_600_000;

/**
 * `at` in `timeZone`, read at `now`: "Wed 3:00 AM MDT", or "Wed, Oct 7, 3:00
 * AM MDT" when it is six days or more away in either direction. `long` spells
 * the weekday out ("Wednesday 3:00 AM MDT").
 */
export function whenInZone(
  at: number | string,
  timeZone: string,
  now: number,
  style: 'short' | 'long' = 'short',
): string {
  const ms = typeof at === 'number' ? at : Date.parse(at);
  const near = Math.abs(ms - now) < WEEKDAY_ALONE_MS;
  return formatInZone(ms, timeZone, {
    weekday: style,
    ...(near ? {} : { month: 'short', day: 'numeric' }),
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * The day `at` falls on in `timeZone`, read at `now`, by the same rule: "Sat",
 * or "Oct 23" six days or more away. No zone name, since a day needs none:
 * what an injury report's day is written with (B-52).
 */
export function dayInZone(at: number | string, timeZone: string, now: number): string {
  const ms = typeof at === 'number' ? at : Date.parse(at);
  const near = Math.abs(ms - now) < WEEKDAY_ALONE_MS;
  return new Intl.DateTimeFormat('en-US', {
    ...(near ? { weekday: 'short' } : { month: 'short', day: 'numeric' }),
    timeZone,
  }).format(new Date(ms));
}
